/**
 * Paths to JSON Converter
 *
 * Конвертирует массив путей в полный JSON объект с данными
 * Используется для подготовки данных к отправке на AI сервер
 *
 * IMPORTANT: Excludes fullInterviewResult from actors to reduce payload size
 * (consistent with SourceDataHydrator behavior)
 */

import type { Audience, Actor } from '@segmently/schema-models/entities';

/**
 * Fields to exclude from Actor when preparing data for AI
 * These fields are too large or not useful for AI processing
 */
const EXCLUDED_ACTOR_FIELDS = ['fullInterviewResult'];

/**
 * Sanitizes an Actor object by removing excluded fields
 * @param actor - Actor object to sanitize
 * @returns Sanitized Actor without excluded fields
 */
function sanitizeActor(actor: Partial<Actor>): Partial<Actor> {
  const sanitized = { ...actor };
  for (const field of EXCLUDED_ACTOR_FIELDS) {
    delete (sanitized as any)[field];
  }
  return sanitized;
}

/**
 * Sanitizes an Audience object by removing excluded fields from its actors
 * @param audience - Audience object to sanitize
 * @returns Sanitized Audience with sanitized actors
 */
function sanitizeAudience(audience: Partial<Audience>): Partial<Audience> {
  if (!audience.actors) {
    return audience;
  }

  return {
    ...audience,
    actors: audience.actors.map(actor => sanitizeActor(actor)) as Actor[],
  };
}

/**
 * Парсит путь формата @audience[ID].actors[ID].field[index]
 */
function parsePath(path: string): {
  audienceId?: string;
  actorId?: string;
  fieldName?: string;
  arrayIndex?: number;
  subcollectionName?: string;
  type: 'audience' | 'actor' | 'field' | 'arrayItem' | 'subcollection';
} | null {
  // Целая audience: @audience[ID]
  const audienceMatch = path.match(/^@audience\[([^\]]+)\]$/);
  if (audienceMatch) {
    return { audienceId: audienceMatch[1], type: 'audience' };
  }

  // Целый actor: @audience[ID].actors[ID]
  const actorMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]$/);
  if (actorMatch) {
    return { audienceId: actorMatch[1], actorId: actorMatch[2], type: 'actor' };
  }

  // Элемент массива: @audience[ID].actors[ID].field[index]
  const arrayItemMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^[]+)\[(\d+)\]$/);
  if (arrayItemMatch) {
    return {
      audienceId: arrayItemMatch[1],
      actorId: arrayItemMatch[2],
      fieldName: arrayItemMatch[3],
      arrayIndex: parseInt(arrayItemMatch[4], 10),
      type: 'arrayItem',
    };
  }

  // Целая subcollection: @audience[ID].actors[ID].fears
  const subcollectionMatch = path.match(/^@audience\[([^\]]+)\]\.actors\[([^\]]+)\]\.([^[]+)$/);
  if (subcollectionMatch) {
    const subcollectionName = subcollectionMatch[3];
    const knownSubcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
    if (knownSubcollections.includes(subcollectionName)) {
      return {
        audienceId: subcollectionMatch[1],
        actorId: subcollectionMatch[2],
        subcollectionName,
        type: 'subcollection',
      };
    } else {
      // Обычное поле actor
      return {
        audienceId: subcollectionMatch[1],
        actorId: subcollectionMatch[2],
        fieldName: subcollectionMatch[3],
        type: 'field',
      };
    }
  }

  // Поле audience: @audience[ID].field
  const audienceFieldMatch = path.match(/^@audience\[([^\]]+)\]\.([^[]+)$/);
  if (audienceFieldMatch) {
    return {
      audienceId: audienceFieldMatch[1],
      fieldName: audienceFieldMatch[2],
      type: 'field',
    };
  }

  return null;
}

/**
 * Конвертирует массив путей в JSON объект с полными данными
 *
 * @param paths - Массив путей формата @audience[ID].actors[ID].field[index]
 * @param audiences - Загруженные audiences с actors и subcollections
 * @returns JSON объект с выбранными данными
 */
export function pathsToJson(paths: string[], audiences: Audience[]): { audiences: Partial<Audience>[] } {
  // Группируем пути по audience ID
  const audienceMap = new Map<string, Set<string>>();

  for (const path of paths) {
    const parsed = parsePath(path);
    if (!parsed || !parsed.audienceId) continue;

    if (!audienceMap.has(parsed.audienceId)) {
      audienceMap.set(parsed.audienceId, new Set());
    }
    audienceMap.get(parsed.audienceId)!.add(path);
  }

  const result: Partial<Audience>[] = [];

  // Обрабатываем каждую audience
  for (const [audienceId, audiencePaths] of audienceMap) {
    const fullAudience = audiences.find((aud) => aud.id === audienceId);
    if (!fullAudience) continue;

    // Проверяем, выбрана ли целая audience
    const hasWholeAudience = Array.from(audiencePaths).some((p) => {
      const parsed = parsePath(p);
      return parsed?.type === 'audience';
    });

    if (hasWholeAudience) {
      // Выбрана целая audience - берем все данные (sanitized)
      result.push(sanitizeAudience(fullAudience));
      continue;
    }

    // Частичный выбор - собираем только выбранные поля
    const partialAudience: Partial<Audience> = {
      id: audienceId,
    };

    // Собираем поля audience уровня
    const audienceFields = new Set<string>();
    const actorPaths = new Map<string, Set<string>>();

    for (const path of audiencePaths) {
      const parsed = parsePath(path);
      if (!parsed) continue;

      if (parsed.type === 'field' && !parsed.actorId) {
        // Поле audience уровня
        audienceFields.add(parsed.fieldName!);
      } else if (parsed.actorId) {
        // Пути связанные с actor
        if (!actorPaths.has(parsed.actorId)) {
          actorPaths.set(parsed.actorId, new Set());
        }
        actorPaths.get(parsed.actorId)!.add(path);
      }
    }

    // Добавляем выбранные поля audience
    for (const fieldName of audienceFields) {
      if (fieldName in fullAudience) {
        (partialAudience as any)[fieldName] = (fullAudience as any)[fieldName];
      }
    }

    // Обрабатываем actors
    if (actorPaths.size > 0 && fullAudience.actors) {
      const partialActors: Partial<Actor>[] = [];

      for (const [actorId, actorPathSet] of actorPaths) {
        const fullActor = fullAudience.actors.find((act) => act.id === actorId);
        if (!fullActor) continue;

        // Проверяем, выбран ли целый actor
        const hasWholeActor = Array.from(actorPathSet).some((p) => {
          const parsed = parsePath(p);
          return parsed?.type === 'actor';
        });

        if (hasWholeActor) {
          // Выбран целый actor (sanitized - без fullInterviewResult)
          partialActors.push(sanitizeActor(fullActor));
          continue;
        }

        // Частичный выбор actor
        const partialActor: Partial<Actor> = {
          id: actorId,
        };

        for (const path of actorPathSet) {
          const parsed = parsePath(path);
          if (!parsed) continue;

          if (parsed.type === 'field') {
            // Поле actor
            if (parsed.fieldName && parsed.fieldName in fullActor) {
              (partialActor as any)[parsed.fieldName] = (fullActor as any)[parsed.fieldName];
            }
          } else if (parsed.type === 'subcollection') {
            // Целая subcollection
            if (parsed.subcollectionName && parsed.subcollectionName in fullActor) {
              (partialActor as any)[parsed.subcollectionName] = (fullActor as any)[parsed.subcollectionName];
            }
          } else if (parsed.type === 'arrayItem') {
            // Отдельный элемент subcollection
            if (parsed.fieldName && parsed.arrayIndex !== undefined) {
              const subcollection = (fullActor as any)[parsed.fieldName];
              if (Array.isArray(subcollection) && subcollection[parsed.arrayIndex]) {
                if (!partialActor[parsed.fieldName as keyof Actor]) {
                  (partialActor as any)[parsed.fieldName] = [];
                }
                (partialActor as any)[parsed.fieldName].push(subcollection[parsed.arrayIndex]);
              }
            }
          }
        }

        partialActors.push(partialActor);
      }

      if (partialActors.length > 0) {
        partialAudience.actors = partialActors as Actor[];
      }
    }

    result.push(partialAudience);
  }

  return { audiences: result };
}
